import clsx from 'clsx'
import type { SearchTileDensity, SearchTileTreatment } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { getSearchTileDensityClassName, getSearchTileTreatmentClassName } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { rem } from 'polished'
import type { PropsWithChildren, ReactElement } from 'react'
import { forwardRef } from 'react'
import styled from 'styled-components'
import SearchTileFeaturedMatchBanner from './shared/SearchTileFeaturedMatchBanner'
import {
  searchTileContainerQueryUp,
  searchTileFeaturedMatchBorderStyles,
  searchTileLayoutStyles,
  searchTileResponsiveStateBorderStyles,
} from './searchTileLayoutStyles'

const Container = styled.div`
  container: search-tile / inline-size;
  display: grid;
  grid-template: 1fr / 1fr;
`

export const _searchTileContainerQueryUp = searchTileContainerQueryUp

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${searchTileFeaturedMatchBorderStyles}

  /* Background variants */
  &.background-default {
    background-color: transparent;
  }
  &.background-highlight-primary {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  ${searchTileResponsiveStateBorderStyles}
`

const Layout = styled.div`
  ${searchTileLayoutStyles(rem(300))}

  &.natural-hero-height {
    grid-template-rows: auto auto;

    ${searchTileContainerQueryUp.desktopSmall} {
      grid-template-rows: auto;
    }
  }
`

const Banner = styled.div`
  &:empty {
    display: none;
  }
`

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  /**
   * @default default
   */
  state?: 'default' | 'highlighted'
  /**
   * Background color variant
   * @default default
   */
  background?: 'default' | 'highlight-primary'
  banner?: ReactElement | false
  className?: string
  /**
   * Presentation-only density for accommodation SERP compact experiment tiles.
   * @default default
   */
  density?: SearchTileDensity
  /**
   * Presentation-only treatment for featured/matched search result tiles.
   * @default default
   */
  treatment?: SearchTileTreatment
  /**
   * Shrinks the tile to fit its content instead of enforcing the standard minimum height.
   * @default false
   */
  fitContent?: boolean
  /** Reserve the hero's intrinsic aspect ratio instead of a fixed-height row. */
  naturalHeroHeight?: boolean
}

const _SearchTileBase = forwardRef<HTMLDivElement, Props>(function _SearchTileBase(
  {
    state = 'default',
    background = 'default',
    banner,
    className,
    density = 'default',
    treatment = 'default',
    fitContent = false,
    naturalHeroHeight = false,
    children,
    ...rest
  },
  ref,
) {
  const isFeaturedMatch = treatment === 'featured-match'
  const densityClassName = getSearchTileDensityClassName(density)
  const treatmentClassName = getSearchTileTreatmentClassName(treatment)

  return <Container {...rest} ref={ref} className={clsx(_SearchTileBase.name, className)}>
    <LayoutWrapper
      className={clsx(
        `state-${state}`,
        `background-${background}`,
        densityClassName,
        treatmentClassName,
      )}
    >
      {(isFeaturedMatch || !!banner) && <Banner>
        {isFeaturedMatch && <SearchTileFeaturedMatchBanner />}
        {banner}
      </Banner>}
      <Layout className={clsx(densityClassName, { 'fit-content': fitContent, 'natural-hero-height': naturalHeroHeight })}>
        {children}
      </Layout>
    </LayoutWrapper>
  </Container>
})

export default _SearchTileBase
